import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { useAdsManagerConfigQuery } from 'src/apollo/onlineOrdering'
import { isPreviewSite } from 'src/lib/js/previewModeUtils'
import { useStaticSiteContext } from 'src/public/components/app/StaticSiteContext'
import { buildCanonicalUrl } from 'src/shared/js/utils/DomainUtils'
import { useSSRAppleBannerContext } from 'src/server/SSRAppleBannerContext'

import { getImageUrl } from 'shared/components/common/Image'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { StructuredDataConsumer } from 'public/components/seo/context'

import { noIndex as noIndexConfig, resources } from 'config'

import PixelsMeta, { PixelsMetaProps } from './PixelsMeta'
import StyleMeta, { StyleMetaProps } from './StyleMeta'
import UserWayMeta, { UserWayMetaProps } from './UserWayMeta'

type Props = {
  description?: string
  icon: string
  favicon?: string
  logo?: string
  heroImgSrc?: string
  title?: string | null
  siteName?: string | null
  domain: string // The current domain being accessed (e.g., restaurant.toast.site, www.restaurant.com)
  titleTag?: string
  noIndex?: boolean
  primaryCustomDomain?: string // The restaurant's preferred custom domain for SEO canonical URLs
  googleSiteVerificationTokens?: string[] // content values for google-site-verification meta tags
} & StyleMetaProps &
  PixelsMetaProps &
  UserWayMetaProps

// Meta does not depend on RestaurantContext since it is shared with Toast Local.
// Toast Local shouldn't have site-specific data included.
// If you want site-specific data in <head>, use SiteMeta.
const Meta = (meta: Props) => {
  const location = useLocation()
  const { appleSmartBannerContent } = useSSRAppleBannerContext()
  const url = `https://${meta.domain}${location.pathname}`
  const canonicalUrl = buildCanonicalUrl(
    url,
    location.pathname,
    meta.primaryCustomDomain
  )
  const title = meta.titleTag || meta.title || ''
  const logoSrc = meta.favicon || meta.icon || meta.logo
  const ogImgSrc = meta.heroImgSrc || logoSrc
  const externalModules = meta.externalModules
  const noIndex = noIndexConfig || meta.noIndex

  return (
    <>
      <Helmet>
        {noIndex ? <meta name='robots' content='noindex' /> : null}
        {meta.description ? (
          <meta name='description' content={meta.description} />
        ) : null}
        {meta.description ? (
          <meta property='og:description' content={meta.description} />
        ) : null}
        <link rel='canonical' href={canonicalUrl} />
        <meta property='og:url' content={url} />
        <meta property='og:type' content='website' />
        {meta.siteName ? (
          <meta property='og:site_name' content={meta.siteName} />
        ) : null}
        {title ? <title>{title}</title> : null}
        {title ? <meta property='og:title' content={title} /> : null}
        {ogImgSrc ? (
          <meta
            property='og:image'
            content={getImageUrl(ogImgSrc, resources.imagePrefix)}
          />
        ) : null}
        {logoSrc ? (
          <link rel='icon' id='favicon' href={getImageUrl(logoSrc)} />
        ) : null}
        {appleSmartBannerContent && (
          <meta name='apple-itunes-app' content={appleSmartBannerContent} />
        )}
        {meta.googleSiteVerificationTokens?.map((token) => (
          <meta key={token} name='google-site-verification' content={token} />
        ))}
      </Helmet>
      <StructuredDataConsumer />
      <PixelsMeta
        pixelsV2={meta.pixelsV2}
        adsPixels={meta.adsPixels}
        adsManagerConversionTagsConfig={
          meta.adsManagerConversionTagsConfig ?? undefined
        }
        hasFullCustomization={meta.hasFullCustomization}
      />
      <StyleMeta {...meta} />
      <UserWayMeta externalModules={externalModules} />
    </>
  )
}

// Meta with site-specific data
export const SiteMeta = (meta: Props) => {
  const { restaurant, ooRestaurant, selectedLocation, toastProduct } =
    useRestaurant()
  const ooClient = useOOClient()
  const { data: adsManagerData } = useAdsManagerConfigQuery({
    skip: !ooRestaurant?.guid,
    variables: { restaurantGuid: ooRestaurant?.guid ?? '' },
    client: ooClient
  })
  const adsManagerConversionTagsConfig =
    adsManagerData?.adsManager?.adsManagerConversionTagsConfig
  // don't index a site in preview mode or oo-only sites that dont have the OO module
  const staticContext = useStaticSiteContext()
  const isPreviewMode = isPreviewSite(
    staticContext,
    restaurant?.config,
    toastProduct
  )
  const ooOnly = restaurant.config.isOnlineOrderingOnly ?? true
  const hasOOModule = ooRestaurant?.hasOnlineOrderingModule ?? true
  const noIndex = isPreviewMode || (ooOnly && !hasOOModule)

  // allow unverified tokens to render for up to 48 hours
  const TWO_DAYS_AGO = Date.now() - 48 * 60 * 60 * 1000
  const googleSiteVerificationTokens = restaurant.seoAnalytics?.gsc
    ?.filter(
      (config) =>
        config?.verified ||
        (config?.generatedAt &&
          new Date(config.generatedAt).getTime() >= TWO_DAYS_AGO)
    )
    .map((config) => config?.htmlMetaVerificationToken?.trim())
    .filter((token): token is string => !!token)

  return (
    <Meta
      {...meta}
      noIndex={noIndex}
      googleSiteVerificationTokens={googleSiteVerificationTokens}
      adsManagerConversionTagsConfig={adsManagerConversionTagsConfig}
      hasFullCustomization={restaurant?.config?.hasFullCustomization}
      title={meta.title || selectedLocation?.name}
      siteName={restaurant.name || selectedLocation?.name}
      logo={ooRestaurant?.logoUrls?.small}
      heroImgSrc={restaurant.config.ooConfig?.heroImage?.displaySrc}
    />
  )
}

export default Meta
